import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { permission: 'accounting.dashboard' },
      },
      {
        path: 'chart-of-accounts',
        name: 'ChartOfAccounts',
        component: () => import('@/views/ChartOfAccountsView.vue'),
        meta: { permission: 'accounting.accounts.view' },
      },
      {
        path: 'journal-entries',
        name: 'JournalEntries',
        component: () => import('@/views/JournalEntriesView.vue'),
        meta: { permission: 'accounting.journal_entries.view' },
      },
      {
        path: 'journal-entries/:id',
        name: 'JournalEntryDetail',
        component: () => import('@/views/JournalEntryDetailView.vue'),
        meta: { permission: 'accounting.journal_entries.view' },
      },
      {
        path: 'general-ledger',
        name: 'GeneralLedger',
        component: () => import('@/views/GeneralLedgerView.vue'),
        meta: { permission: 'accounting.general_ledger.view' },
      },
      {
        path: 'accounts-payable',
        name: 'AccountsPayable',
        component: () => import('@/views/AccountsPayableView.vue'),
        meta: { permission: 'accounting.payable.view' },
      },
      {
        path: 'accounts-receivable',
        name: 'AccountsReceivable',
        component: () => import('@/views/AccountsReceivableView.vue'),
        meta: { permission: 'accounting.receivable.view' },
      },
      {
        path: 'bank-accounts',
        name: 'BankAccounts',
        component: () => import('@/views/BankAccountsView.vue'),
        meta: { permission: 'accounting.reconciliation.view' },
      },
      {
        path: 'bank-reconciliation',
        name: 'BankReconciliation',
        component: () => import('@/views/BankReconciliationView.vue'),
        meta: { permission: 'accounting.reconciliation.view' },
      },
      {
        path: 'financial-statements',
        name: 'FinancialStatements',
        component: () => import('@/views/FinancialStatementsView.vue'),
        meta: { permission: 'accounting.statements.view' },
      },
      {
        path: 'budgets',
        name: 'Budgets',
        component: () => import('@/views/BudgetsView.vue'),
        meta: { permission: 'accounting.budgets.view' },
      },
      {
        path: 'tax-management',
        name: 'TaxManagement',
        component: () => import('@/views/TaxManagementView.vue'),
        meta: { permission: 'accounting.taxes.view' },
      },
      {
        path: 'fixed-assets',
        name: 'FixedAssets',
        component: () => import('@/views/FixedAssetsView.vue'),
        meta: { permission: 'accounting.assets.view' },
      },
      {
        path: 'reports',
        name: 'Reports',
        component: () => import('@/views/ReportsView.vue'),
        meta: { permission: 'accounting.reports.view' },
      },
      {
        path: 'audit-trail',
        name: 'AuditTrail',
        component: () => import('@/views/AuditTrailView.vue'),
        meta: { permission: 'accounting.audit.view' },
      },
      {
        path: 'fiscal-periods',
        name: 'FiscalPeriods',
        component: () => import('@/views/FiscalPeriodsView.vue'),
        meta: { permission: 'accounting.settings.manage' },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { permission: 'accounting.settings.manage' },
      },
      {
        path: 'platform-finance',
        name: 'PlatformFinance',
        component: () => import('@/views/PlatformFinanceView.vue'),
        meta: { permission: 'accounting.dashboard' },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/ProfileView.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const token = localStorage.getItem('va_token')

  if (token && !authStore.user) {
    try {
      await authStore.fetchUser()
    } catch {
      localStorage.removeItem('va_token')
      return next('/login')
    }
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next('/login')
  }

  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    return next('/')
  }

  if (to.meta.permission && !authStore.hasPermission(to.meta.permission)) {
    return next('/login')
  }

  next()
})

export default router
